/**
 * Real Integrations
 * Gerçek servislerle entegrasyon (OpenAI, SendGrid, vb.)
 *
 * Kurulum:
 * npm install openai @sendgrid/mail
 *
 * .env dosyasına ekleyin:
 * VITE_OPENAI_API_KEY=sk-...
 * VITE_SENDGRID_API_KEY=SG....
 * VITE_FROM_EMAIL=noreply@yourdomain.com
 */

// ============================================
// OpenAI LLM Integration
// ============================================

const OPENAI_API_KEY = import.meta.env?.VITE_OPENAI_API_KEY;

/**
 * OpenAI ile LLM çağrısı yapar
 */
export async function InvokeLLM({ prompt, response_json_schema, add_context_from_internet }) {
  if (!OPENAI_API_KEY) {
    console.warn('OpenAI API key bulunamadı, mock data kullanılıyor.');
    const { InvokeLLM: MockLLM } = await import('./_MockIntegrations.js');
    return MockLLM({ prompt, response_json_schema, add_context_from_internet });
  }

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'Du bist ein Business Development Assistent. Antworte immer auf Deutsch und im JSON-Format.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        response_format: { type: 'json_object' },
        temperature: 0.7,
        max_tokens: 2000,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content;

    return JSON.parse(content);
  } catch (error) {
    console.error('OpenAI error:', error);
    // Fallback to mock
    const { InvokeLLM: MockLLM } = await import('./_MockIntegrations.js');
    return MockLLM({ prompt, response_json_schema, add_context_from_internet });
  }
}

// ============================================
// Email Integration (SendGrid / Resend / SMTP)
// ============================================

const SENDGRID_API_KEY = import.meta.env?.VITE_SENDGRID_API_KEY;
const FROM_EMAIL = import.meta.env?.VITE_FROM_EMAIL || 'noreply@example.com';

/**
 * SendGrid ile e-posta gönderir
 */
export async function SendEmail({ to, subject, body, attachments = [] }) {
  if (!SENDGRID_API_KEY) {
    console.warn('SendGrid API key bulunamadı, mock kullanılıyor.');
    const { SendEmail: MockEmail } = await import('./_MockIntegrations.js');
    return MockEmail({ to, subject, body, attachments });
  }

  try {
    const emailData = {
      personalizations: [{ to: [{ email: to }] }],
      from: { email: FROM_EMAIL },
      subject,
      content: [
        {
          type: 'text/plain',
          value: body,
        },
      ],
    };

    // Attachments varsa ekle
    if (attachments.length > 0) {
      emailData.attachments = attachments.map(att => ({
        content: att.content,
        filename: att.filename,
        type: 'application/pdf',
        disposition: 'attachment',
      }));
    }

    const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SENDGRID_API_KEY}`,
      },
      body: JSON.stringify(emailData),
    });

    if (!response.ok && response.status !== 202) {
      throw new Error(`SendGrid error: ${response.status}`);
    }

    return {
      success: true,
      messageId: response.headers.get('X-Message-Id') || `sg_${Date.now()}`,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('SendGrid error:', error);
    throw error;
  }
}

// ============================================
// PDF Generation (Browser-based)
// ============================================

/**
 * HTML'den PDF oluşturur (print dialog ile)
 */
export async function GeneratePDF({ html, filename }) {
  // Browser'da PDF oluşturma (html2pdf veya print)
  try {
    // html2pdf kütüphanesi varsa kullan
    if (typeof window !== 'undefined' && window.html2pdf) {
      const element = document.createElement('div');
      element.innerHTML = html;
      document.body.appendChild(element);

      const opt = {
        margin: 10,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      };

      const pdfBlob = await window.html2pdf().set(opt).from(element).outputPdf('blob');
      document.body.removeChild(element);

      // Blob'u base64'e çevir
      const reader = new FileReader();
      const base64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(pdfBlob);
      });

      return {
        success: true,
        filename,
        pdf_content: base64,
        blob: pdfBlob,
      };
    }

    // Fallback: HTML olarak döndür
    const base64Html = btoa(unescape(encodeURIComponent(html)));
    return {
      success: true,
      filename,
      pdf_content: base64Html,
      url: `data:text/html;base64,${base64Html}`,
      note: 'PDF generation requires html2pdf library. Install with: npm install html2pdf.js',
    };
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
}

import { SupabaseAdapter } from '@/api/adapters/SupabaseAdapter';

// ============================================
// File Upload Integration
// ============================================

/**
 * Dosya yükler (Supabase Storage)
 */
export async function UploadFile({ file }) {
  try {
    const supabase = SupabaseAdapter.getClient();
    
    if (supabase) {
      const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '')}`;
      const { data, error } = await supabase.storage
        .from('documents')
        .upload(fileName, file);

      if (!error) {
        const { data: publicUrlData } = supabase.storage
          .from('documents')
          .getPublicUrl(fileName);
          
        return {
          success: true,
          file_url: publicUrlData.publicUrl,
          filename: fileName
        };
      }
      console.warn('Supabase storage upload failed:', error);
    }
    
    // Fallback: Create Object URL (local only)
    console.warn('Using local object URL for file upload fallback');
    return {
      success: true,
      file_url: URL.createObjectURL(file),
      filename: file.name,
      note: 'File uploaded locally (blob). For persistent storage, configure Supabase Storage bucket "documents".'
    };
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
}

/**
 * Real Integrations Export
 */
export const RealIntegrations = {
  Core: {
    InvokeLLM,
    SendEmail,
    GeneratePDF,
    UploadFile,
  },
};

export default RealIntegrations;
